// covered-by: frontends/carrie/src/routes/add-password/__tests__/view.test.tsx
import {
  type CreatePasswordBody,
  type ShowcaseToken,
} from "@mg/schemas/src/prince/auth";
import { Button, Grid, Text, Flex, TextField } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { jwtDecode } from "jwt-decode";
import { usePostHog } from "posthog-js/react";
import {
  type FormEventHandler,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import zxcvbn from "zxcvbn";

import { addPasswordRoute } from "./route";

import { LoginLayout } from "../../components/layouts/login";
import { PasswordInput } from "../../components/PasswordInput";
import { PasswordRequirements } from "../../components/PasswordRequirements";
import { AUTH_URL } from "../../config/env";
import { useUI } from "../../contexts/ui";
import {
  type UpdatePasswordPayload,
  updatePassword,
} from "../../services/auth";
import { updateMinimalUserProfile } from "../../services/userProfile";
import {
  defaultPostAuthLandingKind,
  ensureDefaultPostAuthLandingPath,
} from "../../utils/defaultPostAuthLanding";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { smartFetch } from "../../utils/http";
import { queryClient } from "../../utils/queryClient";
import {
  consumeShareLinkRedirect,
  stashShareLinkRedirect,
} from "../../utils/shareLinkRedirect";
import { login } from "../../utils/slices/auth";
import { ticketsRoute } from "../tickets/route";

function useUpdatePasswordMutation() {
  return useMutation({
    mutationKey: ["update-password"],
    mutationFn: updatePassword,
  });
}

export function AddPasswordView() {
  const { notify } = useUI();
  const localToken = localStorage.getItem("token");
  const user = useAppSelector((state) => state.auth.value);
  const dispatch = useAppDispatch();
  const queryParams = addPasswordRoute.useSearch();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const mutation = useUpdatePasswordMutation();
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    password: string;
    score: number;
    suggestions: string[];
  }>({
    password: "",
    score: 0,
    suggestions: [],
  });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [nameInput, setNameInput] = useState("");
  // Path A race guard. When the user arrives via `?f=` fast-accept,
  // `smartFetch(/accept-invite)` resolves a token, we `dispatch(login)`,
  // and React schedules a re-render BEFORE the subsequent
  // `await ensureDefaultPostAuthLandingPath(...)` resolves. If the
  // returned user already has `isEnterpriseOnboarded === true` (returning
  // share recipient), the early-return guard below would race ahead and
  // navigate to /tickets, defeating Path A's whole purpose. Keep this
  // flag set until Path A finishes its own navigate() (or hits its
  // error path) so the guard stays suppressed until Path A is done.
  const [fastAcceptInFlight, setFastAcceptInFlight] = useState(() =>
    Boolean(
      (queryParams as Record<string, unknown>).f &&
      (queryParams as Record<string, unknown>).code &&
      (queryParams as Record<string, unknown>).email,
    ),
  );

  // Share-link signup mints users with `name = email` (so downstream
  // populates like ticket participants and comments don't blow up Zod).
  // When that user later lands on the add-password page to complete
  // signup, surface an optional "Full name" field so they can replace
  // the placeholder. The field shows whenever the name is missing,
  // blank, or looks like an email — i.e. anything that isn't clearly
  // a real name. We render eagerly (before `user` is even hydrated
  // from the auth slice) so the field appears on first paint instead
  // of popping in mid-form. The trade-off is that returning users
  // with a real name will see a brief flash; that's acceptable
  // because it disappears as soon as the auth slice resolves.
  const hasRealName =
    !!user?.name && !user.name.includes("@") && user.name.trim().length > 0;
  const showNameField = !hasRealName;

  // Stash the share-link `redirectTo` (if any) before any auto-login reload
  // can unmount this component. We pull it back out of sessionStorage at the
  // post-signup completion sites so a user who comes back via the invite
  // email lands on the originally-shared ticket.
  useEffect(() => {
    stashShareLinkRedirect(
      (queryParams as Record<string, unknown>)?.redirectTo,
    );
  }, []);

  useEffect(() => {
    const f = queryParams.f;
    if (f) {
      const code = queryParams.code;
      const email = queryParams.email as string;
      smartFetch(
        `${AUTH_URL}/accept-invite?code=${code}&email=${encodeURIComponent(email)}&fetch=true`,
        { method: "GET" },
      )
        .then(async (response: any) => {
          const token = response?.token as string | undefined;
          if (!token) {
            setFastAcceptInFlight(false);
            return;
          }
          dispatch(login({ token }));

          // `?f=` is for already-onboarded recipients (returning share-link
          // users / existing members). INVITED users still need to set a
          // password — auto-navigating them into the app left a passwordless
          // session and suppressed the auth-layout redirect via the mount-time
          // `onboarding_flow` flag (resend-invite + onboarding.guard).
          let alreadyOnboarded = false;
          try {
            alreadyOnboarded = Boolean(
              jwtDecode<ShowcaseToken>(token).isEnterpriseOnboarded,
            );
          } catch {
            alreadyOnboarded = false;
          }
          if (!alreadyOnboarded) {
            setFastAcceptInFlight(false);
            return;
          }

          const redirectTo = consumeShareLinkRedirect();
          if (redirectTo) {
            posthog.capture("share_link_signup_completed", {
              redirectTo,
            });
            navigate({ to: redirectTo });
          } else {
            // Cache prime is required: this useEffect fires before
            // `authLayoutRoute.loader` has a chance to prime
            // `brand-guidelines` and `enterprises`, so without
            // ensureDefaultPostAuthLandingPath, `canAccessAskPuntt()`
            // would read undefined → false and every share-link
            // signup would bounce to /tickets even for AP-enabled
            // enterprises.
            const target = await ensureDefaultPostAuthLandingPath(queryClient);
            posthog.capture("post_auth_default_landing", {
              site: "share_link_fast_accept",
              target: defaultPostAuthLandingKind(),
            });
            navigate({ to: target });
          }
          // Intentionally do NOT release `fastAcceptInFlight` on the
          // onboarded success path. `navigate()` unmounts this component;
          // toggling the flag here would trigger one more render in which
          // `user?.isEnterpriseOnboarded && !fastAcceptInFlight` is now
          // true, racing the early-return guard against unmount.
        })
        .catch((error: unknown) => {
          // Before this catch existed, a failure inside
          // `/accept-invite` (network, 4xx, 5xx, Zod parse fail) would
          // silently strand the user on the add-password page with no
          // UI feedback. Surface the error and release the
          // `fastAcceptInFlight` guard so the password form is at
          // least reachable if the user wants to set a password
          // manually instead.
          console.error("share-link fast-accept failed", error);
          notify({
            title: "Oops! Something went wrong.",
            message:
              "We couldn't complete your invite. Please contact the Puntt AI team and we'll get you sorted asap!",
            variant: "error",
          });
          setFastAcceptInFlight(false);
        });
    }
  }, []);

  const checkPasswordStrength = useCallback((password: string) => {
    const evaluation = zxcvbn(password);
    setPasswordStrength({
      suggestions: evaluation.feedback.suggestions,
      score: evaluation.score,
      password,
    });
  }, []);

  const _handlePasswordChange = useCallback(
    (password: string) => {
      if (password.length === 0) {
        setPasswordTouched(false);
        setConfirmTouched(false);
        setPasswordsMatch(false);
      } else if (!passwordTouched) {
        setPasswordTouched(true);
      }
      checkPasswordStrength(password);
    },
    [passwordTouched, passwordStrength, checkPasswordStrength],
  );

  const checkValidity = useCallback(() => {
    if (!formRef.current) return;
    const elements = formRef.current.elements;
    const password = elements.namedItem("password") as HTMLInputElement;
    const confirmPassword = elements.namedItem(
      "confirm_password",
    ) as HTMLInputElement;
    if (password) checkPasswordStrength(password.value);
    if (password && confirmPassword) {
      setPasswordsMatch(password.value === confirmPassword.value);
    }
  }, [checkPasswordStrength]);

  const checkPasswordLength = useCallback(() => {
    if (!formRef.current) return;
    const password = formRef.current.elements.namedItem(
      "password",
    ) as HTMLInputElement;
    return password?.value.length >= 10;
  }, []);

  const handlePasswordUpdate = async (
    data: CreatePasswordBody & UpdatePasswordPayload,
  ) => {
    try {
      await mutation.mutateAsync(data);
      localStorage.setItem("onboarding_flow", "true");

      // If the user typed a real name in the optional field, persist it
      // now that they're authenticated. We only fire when the field was
      // visible (i.e. they don't already have a real name on file) and
      // they actually typed something. Best-effort: a failure here
      // shouldn't block them from finishing signup — they can always
      // edit it later from the profile dialog.
      const trimmedName = nameInput.trim();
      if (showNameField && trimmedName) {
        try {
          await updateMinimalUserProfile({ name: trimmedName });
        } catch (e) {
          console.error("Failed to set name during share-link signup", e);
        }
      }

      const redirectTo = consumeShareLinkRedirect();
      if (redirectTo) {
        posthog.capture("share_link_signup_completed", {
          redirectTo,
        });
        navigate({ to: redirectTo });
      } else {
        // Same cache-prime rationale as Path A: this completion site
        // is also outside `authLayoutRoute.loader`'s cache-prime
        // chain, so we must `await ensureDefaultPostAuthLandingPath`
        // before navigating — otherwise an AP-enabled enterprise's
        // user lands on /tickets via the `askPunttRoute.beforeLoad`
        // bounce.
        const target = await ensureDefaultPostAuthLandingPath(queryClient);
        posthog.capture("post_auth_default_landing", {
          site: "share_link_password_set",
          target: defaultPostAuthLandingKind(),
        });
        navigate({ to: target });
      }
    } catch (error: any) {
      let message =
        "Please contact the Puntt AI team and we'll get you sorted asap!";
      const resp = await error.json();
      if ([400, 404, 409].includes(error?.status) && resp?.message) {
        message = resp.message;
      }
      setSubmitting(false);
      notify({
        title: "Oops! Something went wrong.",
        message,
        variant: "error",
      });
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target as HTMLFormElement);
    formData.set("password", formData.get("password") as string);
    formData.delete("confirm_password");
    const data = Object.fromEntries(formData.entries()) as CreatePasswordBody &
      UpdatePasswordPayload;
    const code = queryParams.code;
    const email = queryParams.email as string;
    if (user && user.email) {
      handlePasswordUpdate(data);
      return;
    }
    try {
      const response: any = await smartFetch(
        `${AUTH_URL}/accept-invite?code=${code}&email=${encodeURIComponent(email)}&fetch=true`,
        { method: "GET" },
      );
      const token = response?.token;
      dispatch(login({ token }));
      await handlePasswordUpdate(data);
    } catch (err: any) {
      setSubmitting(false);
      let message =
        "Please contact the Puntt AI team and we'll get you sorted asap!";
      const resp = await err.json();
      if ([400, 404, 409].includes(err?.status) && resp?.message) {
        message = resp.message;
      }
      notify({
        title: "Oops! Something went wrong.",
        message,
        variant: "error",
      });
    }
  };

  // Suppress this early-return when Path A is still in flight — see the
  // `fastAcceptInFlight` comment up top. Without this, a returning
  // share-link recipient (already `isEnterpriseOnboarded`) would race
  // ahead to /tickets before Path A's `await
  // ensureDefaultPostAuthLandingPath(queryClient)` resolves and navigates
  // to the correct default landing.
  if (user?.isEnterpriseOnboarded && !fastAcceptInFlight) {
    return (
      <Navigate
        to={ticketsRoute.to}
        search={({ code, email, f, ...rest }) => rest}
      />
    );
  }

  return (
    <LoginLayout>
      <div className="w-full max-w-md">
        <Text
          weight="medium"
          size="6"
          className="mb-2 block break-words text-xl tracking-tight"
          data-testid="create-account-description"
        >
          Set a password for your new account.
        </Text>
        <form
          onSubmit={handleSubmit}
          ref={formRef}
          className="grid gap-6"
          data-testid="add-password-form"
          aria-label="Create account"
        >
          <input
            type="hidden"
            name="token"
            value={localToken ?? ""}
            data-testid="add-password-token-field"
            aria-hidden="true"
          />
          {showNameField && (
            <Grid gap="2">
              <Flex justify="between" align="baseline">
                <Text weight="medium">Full name</Text>
                <Text size="1" color="gray">
                  Optional
                </Text>
              </Flex>
              <TextField.Root
                name="name"
                id="name"
                placeholder="What should we call you?"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                disabled={mutation.isPending || submitting}
                autoComplete="name"
                data-testid="name-field"
              />
            </Grid>
          )}
          <Grid gap="2">
            <Text weight="medium">New Password</Text>
            <PasswordInput
              required
              placeholder="Enter your password"
              name="password"
              id="password"
              minLength={10}
              disabled={mutation.isPending}
              autoComplete="new-password"
              data-testid="password-field"
              onChange={(e) => {
                if (e.target.value.length === 0) {
                  setPasswordTouched(false);
                } else if (!passwordTouched) {
                  setPasswordTouched(true);
                }

                try {
                  const evaluation = zxcvbn(e.target.value);
                  setPasswordStrength({
                    suggestions: evaluation.feedback.suggestions,
                    score: evaluation.score,
                    password: e.target.value,
                  });
                } catch (error) {
                  console.error("Password strength evaluation failed:", error);
                  // Fallback to basic validation
                  setPasswordStrength({
                    suggestions: [],
                    score: e.target.value.length >= 10 ? 2 : 0,
                    password: e.target.value,
                  });
                }
              }}
              error={
                passwordTouched &&
                (mutation.isError ||
                  !checkPasswordLength() ||
                  passwordStrength.score < 3)
              }
            />
          </Grid>
          <PasswordRequirements
            strength={passwordStrength}
            showInitialRequirements={!passwordTouched}
          />

          {passwordTouched && !checkPasswordLength() && (
            <Text size="2" className="text-puntt-red-10">
              Password must be at least 10 characters long
            </Text>
          )}

          <Grid gap="2">
            <Text weight="medium">Verify New Password</Text>
            <PasswordInput
              required
              placeholder="Enter password again"
              variant="classic"
              data-testid="confirm-password-field"
              onInput={(e) => {
                const value = (e.target as HTMLInputElement).value;
                if (value.length === 0) {
                  setConfirmTouched(false);
                  setPasswordsMatch(false);
                } else if (!confirmTouched) {
                  setConfirmTouched(true);
                }
                checkValidity();
              }}
              name="confirm_password"
              id="confirm_password"
              minLength={10}
              disabled={mutation.isPending}
              autoComplete="new-password"
              error={confirmTouched && !passwordsMatch}
            />
            {confirmTouched && !passwordsMatch && (
              <Flex align="center" gap="2">
                <Text size="2" className="text-puntt-red-10">
                  Passwords do not match
                </Text>
              </Flex>
            )}
          </Grid>
          <Button
            type="submit"
            disabled={
              !passwordsMatch ||
              submitting ||
              mutation.isPending ||
              passwordStrength.score < 3 ||
              !checkPasswordLength()
            }
            loading={mutation.isPending || submitting}
            data-testid="form-submit-cta"
          >
            Sign Up
          </Button>
        </form>
      </div>
    </LoginLayout>
  );
}
